import {reactive, computed, ref} from 'vue';
import {formatPrice, getValueFromMap} from '@/utils/utils';
import {getDiscount, getGift} from "@/api/api";

export const useConfiguration = function (initialConfig, router, route) {
    const emptyDiscount = {
        success: false,
        message: null,
        discount: {
            code: null,
            discount: 0,
            fixedDiscount: 0
        }
    };
    const configuration = reactive({
        ...initialConfig,
        discount: JSON.parse(JSON.stringify(emptyDiscount)),
        usedDiscountCode: null
    });

    const gift = ref(null);

    let lastTotalPrice = 0;

    const configurationEntries = computed(() => {
        if (!configuration.paths[route.name]) {
            return {};
        }
        return configuration.paths[route.name].steps.reduce((acc, step) => ({...acc, ...step.entries}), {});
    });

    const activePackage = computed(() => {
        if(!configuration.discountPackages) {
            return null;
        }
        const activePackage = configuration.discountPackages.fields.find(p => p.id === configuration.discountPackages.selected);
        return {
            ...activePackage, helperDiscount: activePackage.multiplier * (100 - activePackage.percentage) / 100
        }
    });

    const sortedPackages = computed(() => {
        return configuration.discountPackages.fields
            .map(field => ({...field, helperDiscount: field.multiplier * (100 - field.percentage) / 100}))
            .sort((a, b) => b.helperDiscount - a.helperDiscount);
    });

    const getDeliveryCoefficient = computed(() => {
        return parseFloat(configurationEntries?.value['ConfiguratorDeliveryCoefficient']?.fields[0]?.value ?? '0');
    });

    const getEntryResult = (entry, entryId) => {
        let priceMultiplier = 1;
        let percentageDiscount = 0;
        let entryDeliveryMultiplier = entry.deliveryCoefficient ?? 1;
        let entryPrice = null;
        let entryFinalPercentage = null;
        let entryParts = [];
        let entryFrequency = null;
        let isPriceFrom = false;
        let mainValue = null;

        if (entry.packages) {
            for (const p of sortedPackages.value) {
                if (p.helperDiscount <= activePackage.value.helperDiscount && entry.packages.includes(p.id)) {
                    priceMultiplier = p.multiplier;
                    if(!entry.disablePercentageCount) {
                        percentageDiscount = p.percentage;
                    }
                    entryFrequency = p.multiplier;
                    break;
                }
            }
        }

        entry.fields && Object.values(entry.fields).forEach(mainField => {
            const mergedFields = mainField.subFields ? [mainField, ...mainField.subFields] : [mainField];
            let fixedPrice = null;
            let addPriceFromEntry = null;
            let unitPrice = null;
            let unitMultiplier = 1;
            let value =
                mainField.valueFrom
                    ? configurationEntries.value[mainField.valueFrom].fields[0].value
                    : mainField.hasOwnProperty('value') ? mainField.value : null;

            // haack, uchopeno ze spatne strany
            if (mainField.type === 'package') {
                value = activePackage.value.name;
            }

            if (value !== null && mainField.type !== 'check' && mainField.type !== 'check-extended') {
                const conversion = mainField.conversion || 1;

                if (mainField.type !== 'select' && mainField.type !== 'radio-image' && mainField.type !== 'text' && mainField.type !== 'package' && mainField.type !== 'range') {
                    const prefix = mainField.prefix ? (mainField.prefix + (!mainField.prefix.endsWith(":") ? ': ': ' ')) : '';
                    const unit = mainField.unitConversion || mainField.unit || '';
                    if (value) {
                        entryParts.push(prefix + (Math.round(((value * conversion) + Number.EPSILON) * 100) / 100) + ' ' + unit);
                    }
                }
            }

            mergedFields.forEach(field => {
                if ((field.type === 'check' || field.type === 'check-extended') && !field.value) {
                    return;
                }

                let fieldPricing = null;
                let addedPrice = null;

                if (field.addedPrice) {
                    addedPrice = field.addedPrice;
                }

                if (field.addEntryPrice) {
                    const {price: addEntryCalcedPrice} = getEntryResult(configurationEntries.value[field.addEntryPrice], field.addEntryPrice);
                    addPriceFromEntry = addEntryCalcedPrice / 2;
                }

                if (field.priceMap) {
                    fieldPricing = getValueFromMap(field.priceMap, +value, true);
                } else if (field.price) {
                    fieldPricing = +field.price;
                } else if (field.options && (field.value !== null)) {
                    fieldPricing = +field.value;
                } else if (field.percentageMap) {
                    fieldPricing = getValueFromMap(field.percentageMap, +value, true);
                }

                const prefix = field.prefix ? (field.prefix + (!field.prefix.endsWith(":") ? ': ': ' ')) : '';

                if (field.type === 'check-extended') {
                    const allFields = configurationEntries.value[entryId].fields;
                    const allFieldsSelected = allFields.reduce((acc, f) => acc && f.value, true);
                    if(field.divider && allFieldsSelected) {
                        fieldPricing = fieldPricing / field.divider;
                    }
                    entryParts.push(field.label + ' ' + formatPrice(Math.round(fieldPricing)));
                }
                if (field.type === 'check') {
                    entryParts.push(field.label);
                }
                else if (field.type === 'select' || field.type === 'radio-image') {
                    const selectedOption = field.options.find(f => f.value == field.value)
                    selectedOption && entryParts.push(prefix + selectedOption.label);
                }
                else if (field.type === 'text' || (field.type === 'range' && !field.hasOwnProperty('valuesMap'))) {
                    entryParts.push(prefix + field.value);
                }
                else if (field.type === 'package') {
                    entryParts.push(value);
                }
                else if (field.type === 'range' && field.hasOwnProperty('valuesMap')) {
                    entryParts.push(field.valuesMap[field.value]);
                }

                if (field.type === 'number') {
                    if (((field.min !== undefined && field.min !== null) && field.min > value) || (field.max && field.max < value)) {
                        fieldPricing = null;
                    }
                    if (mainField.type === 'number-to-square-calculated-from-subfields' && field.prefix) {
                        entryParts.push(prefix + field.value);
                    }
                }

                if (fieldPricing !== null) {
                    if (field.pricingType === 'unit-price') {
                        unitPrice += fieldPricing;
                    } else if (field.pricingType === 'field-price') {
                        fixedPrice += fieldPricing;
                    } else if (field.pricingType === 'final-percentage') {
                        entryFinalPercentage += fieldPricing;
                    } else if (field.pricingType === 'unit-with-added-price') {
                        unitPrice += fieldPricing;
                        fixedPrice += addedPrice;
                    } else if (field.pricingType === 'field-price-from') {
                        fixedPrice += fieldPricing;
                        isPriceFrom = true;
                    }
                }

                if (field.unitMultiplier) {
                    unitMultiplier = field.unitMultiplier;
                }
            });

            entryPrice += fixedPrice + ((unitPrice + addPriceFromEntry) * ((mainField.type === 'text' ? 1 : value) * unitMultiplier));
        });

        const priceMultiplied = entryPrice * priceMultiplier + entryPrice * (getDeliveryCoefficient.value * entryDeliveryMultiplier);
        const priceFinalDiscounted = priceMultiplied - (priceMultiplied * (percentageDiscount / 100));

        const zone = (typeof entry.zone !== 'undefined') ? configuration.zones.find(o => o.key === entry.zone).title : null;
        const image = null;

        if (entryParts.length > 0) {
            mainValue = entryParts.shift();
        }

        return {
            zone: zone,
            originPrice: percentageDiscount ? Math.round(priceMultiplied) : null,
            price: priceFinalDiscounted === 0 ? null : Math.round(priceFinalDiscounted),
            finalPercentage: entryFinalPercentage,
            mainValue: mainValue,
            parts: entryParts,
            frequency: entryFrequency,
            isPriceFrom: isPriceFrom,
            customerNotePrompt: entry.customerNotePrompt ?? null,
            customerNote: entry.customerNote,
            image: image
        }
    }

    const resultConfig = computed(() => {
        const result = Object.entries(configurationEntries.value).reduce((acc, [key, entry]) => {
            if (key.match(/Zone$/)) {
                return acc;
            }
            if (entry.key !== 'bundles-0') {
                if (!entry.selected && entry.selected !== undefined) {
                    return acc;
                }
            } else {
                if (!entry.selected) {
                    return acc;
                }
            }
            const {
                zone,
                originPrice,
                price,
                finalPercentage,
                mainValue,
                parts,
                frequency,
                isPriceFrom,
                customerNotePrompt,
                customerNote,
                image
            } = getEntryResult(entry, key);
            if (price === null && finalPercentage === null && !entry.title) {
                return acc;
            }
            return {
                ...acc,
                [key]: {
                    label: entry.title,
                    zone,
                    originPrice,
                    price,
                    finalPercentage,
                    mainValue: mainValue,
                    parts: parts,
                    frequency,
                    isPriceFrom,
                    customerNotePrompt,
                    customerNote,
                    image
                }
            }
        }, {});
        // console.log('resultConfig', result); //debug
        return result;
    });

    function updateGift(configuratorId, orderValue) {
        gift.value = null;
        getGift(configuratorId, orderValue)
            .then(giftResult => {
                if (giftResult.success) {
                    gift.value = giftResult.gift;
                } else {
                    gift.value = null;
                }
            })
            .catch(err => gift.value = null); // TODO co když selže připojení na server?
    }

    const removeDiscount = () => {
        configuration.discount = JSON.parse(JSON.stringify(emptyDiscount));
    };

    const updateDiscount = configuration.updateDiscount = () => {
        getDiscount(configuration.configuratorId, configuration.usedDiscountCode)
            .then(resultDiscount => {
                configuration.discount.success = resultDiscount.success;
                configuration.discount.message = resultDiscount.message;
                configuration.discount.discount = resultDiscount.discount ?? emptyDiscount.discount;
            })
            .catch(() => {
                removeDiscount();
                configuration.usedDiscountCode = null;
            });
    }

    const setDiscountCode = configuration.setDiscountCode = (discountCode) => {
        configuration.usedDiscountCode = discountCode;
        removeDiscount();
        updateDiscount();
    };

    const removeDiscountCode = configuration.removeDiscountCode = () => {
        configuration.usedDiscountCode = null;
        removeDiscount();
    };

    const totalConfig = computed(() => {
        let totalPercentage = 0;
        let totalPriceAddition = 0;
        let totalPriceBase = 0;
        let totalPrice;
        let amountSaved = 0;
        let modifiers = [];

        Object.values(resultConfig.value).forEach(entry => {
            if (entry.price !== null && !isNaN(entry.price)) {
                totalPriceBase += entry.price === -1 ? 0 : entry.price;
            }
            if (entry.finalPercentage !== null && !isNaN(entry.finalPercentage)) {
                totalPercentage += entry.finalPercentage;
            }
        });

        if (totalPercentage) {
            totalPriceAddition = Math.round(totalPriceBase * (totalPercentage / 100));
            totalPrice = Math.round(totalPriceBase + totalPriceAddition);
        } else {
            totalPrice = Math.round(totalPriceBase);
        }

        if (configuration.finalPriceModifier?.length) {
            configuration.finalPriceModifier.forEach(modifier => {
                if (!modifier.percentageMap.length) return;

                const modifierPercentage = getValueFromMap(modifier.percentageMap, totalPriceBase);
                modifiers.push({
                    title: modifier.title,
                    percentage: modifierPercentage,
                    price: Math.round((totalPrice * (modifierPercentage / 100)))
                });
            });
        }

        const minimumFinalPrice = configuration.minimumFinalPrice ?? 0;

        let totalPriceWithModifiers = totalPrice + modifiers.reduce((sum, modifier)  => { return sum + modifier.price }, 0);

        if (Math.abs(totalPriceWithModifiers - lastTotalPrice) > 0.1) {
            lastTotalPrice = totalPriceWithModifiers;
            updateGift(configuration.configuratorId, totalPriceWithModifiers);
        }


        // tady se přepočítá totalPriceWithModifiers podle slevy
        if (configuration.discount.success) {
            if (configuration.discount.discount.fixedDiscount) {
                totalPriceWithModifiers = Math.max(0, totalPriceWithModifiers - configuration.discount.discount.fixedDiscount);
            } else if (configuration.discount.discount.discount) {
                amountSaved = (totalPriceWithModifiers / 100) * configuration.discount.discount.discount;
                totalPriceWithModifiers = totalPriceWithModifiers * (100 - configuration.discount.discount.discount) / 100;
            }
        }

        // přepočítání částky bez DPH
        let totalPriceWithoutVat = Math.round(totalPriceWithModifiers / (1 + configuration.sourceVatLevel / 100));
        let totalPriceWithCurrentVat = Math.round(totalPriceWithoutVat * (1 + configuration.currentVatLevel / 100));

        return {
            totalPriceAddition,
            totalPriceAdditionPercentage: totalPercentage,
            totalPriceBase,
            modifiers,
            minimumFinalPrice,
            totalPriceWithoutVat: totalPriceWithoutVat,
            totalPriceWithCurrentVat: totalPriceWithCurrentVat,
            totalPrice: totalPriceWithModifiers,
            amountSaved,
            gift
        }
    });

    const stepSettings = computed(() => {
        if (!configuration.paths[route.name]) {
            return null;
        }

        return configuration.paths[route.name].steps.map(step => {
            let requiredValidCount = 0;
            let isStepValid = Object.entries(step.entries).reduce((acc, [key, entry]) => {
                if (!entry.required) {
                    return acc;
                }
                const isEntryValid =
                    (resultConfig.value[key]?.price === null || resultConfig.value[key]?.finalPercentage === null) &&
                    (!entry.hasOwnProperty('requireType') || (entry.requireType === "not-empty" && resultConfig.value[key]?.mainValue?.length > 0) || (entry.requireType === "selected" && entry.selected));

                if (step.minRequired) {
                    if (isEntryValid)
                        requiredValidCount++;

                    return acc;
                }

                return acc && isEntryValid;
            }, true);

            if (isStepValid && step.minRequired) {
                isStepValid = isStepValid && (requiredValidCount >= step.minRequired)
            }

            return {
                title: step.title,
                showSummary: step.showSummary ?? true,
                showComparison: step.showEntryComparison ?? false,
                showSectionNav: step.showSectionNav ?? false,
                valid: isStepValid
            }
        });
    });

    return {
        configuration,
        resultConfig,
        totalConfig,
        stepSettings,
        configurationEntries,
        gift
    }
}


export default useConfiguration;
